<template>
  <div class="wap-overlay" @click.self="$emit('close')">
    <div class="wap-phone">
      <div class="wap-header">
        <div class="wap-avatar">N</div>
        <div class="wap-header-text">
          <div class="wap-name">{{ journeyName }}</div>
          <div class="wap-status">{{ statusText }}</div>
        </div>
        <nv-select v-if="langOptions.length > 1" class="wap-lang-select" :value="lang" title="Preview in a different language" @change="onLangChange($event.target.value)">
          <option v-for="o in langOptions" :key="o.code" :value="o.code">{{ o.label }}</option>
        </nv-select>
        <button class="wap-close" title="Close" @click="$emit('close')">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <div ref="body" class="wap-body">
        <div v-for="(m, i) in messages" :key="i" class="wap-bubble" :class="m.role">{{ m.text }}<div class="wap-time">{{ m.time }}</div></div>
        <div v-if="pendingOptions.length" class="wap-options">
          <button v-for="(o, i) in pendingOptions" :key="i" class="wap-opt-btn" @click="chooseOption(o)">{{ o.label }}</button>
        </div>
        <div v-if="ended" class="wap-end-note">Conversation ended</div>
        <div v-if="!currentStageName" class="wap-end-note">This journey has no stages yet</div>
      </div>

      <div class="wap-foot">
        <button class="wap-restart-btn" @click="restart">↻ Restart conversation</button>
      </div>
    </div>
  </div>
</template>

<script>
function archOf (s) {
  if (s.nextApp || (s.stageName === 'end' && !s.next)) return 'end'
  if (s.processingFunctionConfig) return 'pf'
  if (s.input && Array.isArray(s.options) && s.options.length) return 'choice'
  if (s.input) return 'text'
  return 'info'
}
function msgText (stage, lang) {
  for (const t of (stage.text || [])) { for (const rl of Object.values(t)) { if (rl[lang]) return rl[lang] } }
  return ''
}
function cleanMsg (txt) {
  return txt.replace(/\{\{[^}]*\}\}/g, '').replace(/\[\[[^\]]*\]\]/g, '').replace(/\n{3,}/g, '\n\n').trim()
}
function formatTime () {
  const d = new Date()
  let h = d.getHours() % 12
  if (h === 0) h = 12
  const m = String(d.getMinutes()).padStart(2, '0')
  return `${h}:${m} ${d.getHours() >= 12 ? 'PM' : 'AM'}`
}

export default {
  name: 'whatsapp-preview',
  props: {
    journey: { type: Object, default: null },
    currentLang: { type: String, default: 'eng' },
    langOptions: { type: Array, default: () => [] }
  },
  data () {
    return {
      lang: this.currentLang,
      currentStageName: null,
      messages: [],
      pendingOptions: [],
      ended: false
    }
  },
  computed: {
    journeyName () {
      return (this.journey && this.journey.name) || 'Journey Preview'
    },
    statusText () {
      if (this.ended) return 'conversation ended'
      if (this.pendingOptions.length) return 'waiting for reply'
      return 'online'
    }
  },
  mounted () {
    this.restart()
  },
  methods: {
    onLangChange (code) {
      this.lang = code
      this.restart()
    },
    restart () {
      this.messages = []
      this.pendingOptions = []
      this.ended = false
      const stages = (this.journey && this.journey.stages) || {}
      this.currentStageName = stages.uninitialized ? 'uninitialized' : (Object.keys(stages)[0] || null)
      this.advance()
    },
    advance () {
      const stages = (this.journey && this.journey.stages) || {}
      const stage = stages[this.currentStageName]
      if (!stage) return

      const text = cleanMsg(msgText(stage, this.lang))
      if (text) {
        this.messages.push({ role: 'bot', text, time: formatTime() })
        this.scrollToBottom()
      }

      if (stage.nextApp) {
        this.ended = true
        return
      }

      if (archOf(stage) === 'choice') {
        this.pendingOptions = stage.options
          .map((opt, i) => ({ label: opt.replace(/_/g, ' '), next: Array.isArray(stage.next) ? stage.next[i] : null }))
          .filter(o => o.next && stages[o.next])
        return
      }

      let nextName = null
      if (typeof stage.next === 'string' && stage.next) nextName = stage.next
      else if (Array.isArray(stage.next) && stage.next.length) nextName = stage.next.find(n => stages[n]) || null

      if (nextName && stages[nextName]) {
        this.currentStageName = nextName
        setTimeout(() => this.advance(), 450)
      }
    },
    chooseOption (option) {
      const stages = (this.journey && this.journey.stages) || {}
      this.pendingOptions = []
      this.messages.push({ role: 'user', text: option.label, time: formatTime() })
      this.scrollToBottom()
      if (option.next && stages[option.next]) {
        this.currentStageName = option.next
        setTimeout(() => this.advance(), 350)
      }
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.body
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.wap-overlay{
  position:fixed;inset:0;background:rgba(10,18,32,0.5);z-index:2000;
  display:flex;align-items:center;justify-content:center;
}
.wap-phone{
  width:380px;height:680px;max-height:88vh;
  background:#e5ddd5;border-radius:16px;overflow:hidden;
  display:flex;flex-direction:column;box-shadow:0 30px 80px rgba(0,0,0,0.4);
}
.wap-header{
  background:#075e54;color:#fff;padding:12px 14px;
  display:flex;align-items:center;gap:10px;flex-shrink:0;
}
.wap-avatar{
  width:36px;height:36px;border-radius:50%;flex-shrink:0;
  background:linear-gradient(135deg,#14b8a6,#0d9488);
  display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;
}
.wap-header-text{flex:1;min-width:0}
.wap-name{font-size:13.5px;font-weight:700;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.wap-status{font-size:10.5px;color:rgba(255,255,255,0.75)}
.wap-close{
  background:none;border:none;color:#fff;cursor:pointer;width:26px;height:26px;
  display:flex;align-items:center;justify-content:center;border-radius:6px;flex-shrink:0;
}
.wap-close:hover{background:rgba(255,255,255,0.15)}
.wap-lang-select{
  flex-shrink:0;max-width:96px;font-size:11px;font-weight:600;color:#fff;
  background:rgba(255,255,255,0.14);border:1px solid rgba(255,255,255,0.25);border-radius:6px;
  padding:4px 6px;cursor:pointer;
}
.wap-lang-select:focus{outline:none;border-color:rgba(255,255,255,0.5)}
.wap-lang-select option{color:#1a1d23}

.wap-body{
  flex:1;overflow-y:auto;padding:14px 10px;display:flex;flex-direction:column;gap:8px;
  background-image:repeating-linear-gradient(0deg,rgba(0,0,0,0.015) 0 2px,transparent 2px 4px);
}
.wap-body::-webkit-scrollbar{width:6px}
.wap-body::-webkit-scrollbar-thumb{background:rgba(0,0,0,0.15);border-radius:3px}

.wap-bubble{
  max-width:82%;padding:7px 10px 8px;border-radius:8px;font-size:12.8px;line-height:1.5;
  white-space:pre-wrap;box-shadow:0 1px 1px rgba(0,0,0,0.08);
}
.wap-bubble.bot{background:#fff;align-self:flex-start;border-top-left-radius:2px}
.wap-bubble.user{background:#dcf8c6;align-self:flex-end;border-top-right-radius:2px}
.wap-time{font-size:9.5px;color:rgba(0,0,0,0.35);text-align:right;margin-top:3px}

.wap-options{display:flex;flex-direction:column;gap:6px;align-self:flex-end;max-width:88%}
.wap-opt-btn{
  background:#fff;border:1px solid #d9d9d9;color:#0b7a70;font-weight:700;font-size:12.3px;
  padding:8px 12px;border-radius:8px;cursor:pointer;text-align:center;
}
.wap-opt-btn:hover{background:#f5fffc}

.wap-end-note{
  align-self:center;font-size:11px;color:rgba(0,0,0,0.45);background:rgba(255,255,255,0.7);
  padding:4px 12px;border-radius:999px;margin:4px 0;
}

.wap-foot{padding:10px;background:#f0f0f0;flex-shrink:0}
.wap-restart-btn{
  width:100%;text-align:center;background:#075e54;color:#fff;border:none;padding:9px;
  border-radius:22px;font-size:12.5px;font-weight:700;cursor:pointer;
}
.wap-restart-btn:hover{background:#0a7a6a}
</style>



// WEBPACK FOOTER //
// WhatsAppPreview.vue?713c2bee